import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useFirebase } from 'react-redux-firebase'
import { useNavigate } from 'react-router-dom'
import { PageCenteredSection, PageContentShell, PageViewport } from '../../components/PageLayout'
import { ACTIVATE_ACCOUNT_ROUTE, API_PATH_PORTAL_CLAIM_ACCESS, PORTAL_ROUTE } from '../../config'
import { getTopbarHeight, useDimensions } from '../../hooks/useDimensions'
import { AppBarComponent } from '../AppBar'
import { FooterBarComponent } from '../FooterBar'
import { getAuthHeaders, getLocalServerURL } from '../../utils/getApiConfig'
import { onError } from '../../utils/onError'
import { onSuccess } from '../../utils/onSuccess'
import { getIsAuthenticated } from '../App/selectors'

type AuthMode = 'create' | 'login'

export const PortalAccessClaimComponent: React.FC = () => {
  const firebase = useFirebase()
  const navigate = useNavigate()
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { height, width } = useDimensions()
  const topbarHeight = getTopbarHeight(width)
  const [authMode, setAuthMode] = React.useState<AuthMode>('create')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [passwordConfirm, setPasswordConfirm] = React.useState('')
  const [claimCode, setClaimCode] = React.useState('')
  const [isBusy, setIsBusy] = React.useState(false)
  const [infoMessage, setInfoMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    const currentUser = firebase.auth().currentUser
    if (currentUser?.email) {
      setEmail(currentUser.email)
    }
  }, [firebase])

  const handleAuthenticate = React.useCallback(async () => {
    setIsBusy(true)
    setInfoMessage(null)
    try {
      if (authMode === 'create') {
        if (password !== passwordConfirm) {
          onError('Passwords do not match')
          return
        }
        await firebase.auth().createUserWithEmailAndPassword(email, password)
      } else {
        await firebase.auth().signInWithEmailAndPassword(email, password)
      }
      setInfoMessage('Authentication complete. Enter the claim code to link your account.')
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to authenticate')
    } finally {
      setIsBusy(false)
    }
  }, [authMode, email, firebase, password, passwordConfirm])

  const handleClaimAccess = React.useCallback(async () => {
    setIsBusy(true)
    try {
      const headers = await getAuthHeaders()
      if (headers == null) {
        onError('Could not authorize account claim')
        return
      }

      const response = await fetch(
        getLocalServerURL(API_PATH_PORTAL_CLAIM_ACCESS),
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            email,
            claimCode: claimCode.trim().toUpperCase(),
          }),
        }
      )

      if (!response.ok) {
        const errorBody = (await response.json().catch(() => ({}))) as {
          error?: string
          message?: string
        }
        throw new Error(errorBody.error || errorBody.message || 'Failed to link portal access')
      }

      onSuccess('Portal access linked successfully')
      navigate(PORTAL_ROUTE)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to link portal access')
    } finally {
      setIsBusy(false)
    }
  }, [claimCode, email, navigate])

  const handleSignOut = React.useCallback(async () => {
    await firebase.logout()
    setInfoMessage(null)
  }, [firebase])

  return (
    <>
      <Helmet>
        <title>Activate Account || JustLockIt</title>
        <meta
          name="description"
          content="Claim portal access for a phone-created JustLockIt account."
        />
      </Helmet>
      <AppBarComponent />
      <PageViewport viewportHeight={height}>
        <PageContentShell topbarHeight={topbarHeight}>
          <PageCenteredSection>
            <Card sx={{ width: '100%', maxWidth: 720 }}>
              <CardHeader
                title="Claim Portal Access"
                subheader="Use the claim code provided by Just Lock It to link your online login to your existing storage account."
              />
              <Divider />
              <CardContent>
                <Stack spacing={3}>
                  {infoMessage ? <Alert severity="info">{infoMessage}</Alert> : null}
                  {!isAuthenticated ? (
                    <>
                      <Tabs
                        value={authMode}
                        onChange={(_event, value: AuthMode) => setAuthMode(value)}
                      >
                        <Tab value="create" label="Create login" />
                        <Tab value="login" label="Sign in" />
                      </Tabs>
                      <Stack spacing={2}>
                        <TextField
                          label="Email"
                          type="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Password"
                          type="password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          fullWidth
                        />
                        {authMode === 'create' ? (
                          <TextField
                            label="Confirm password"
                            type="password"
                            value={passwordConfirm}
                            onChange={(event) => setPasswordConfirm(event.target.value)}
                            fullWidth
                          />
                        ) : null}
                        <Button
                          variant="contained"
                          onClick={() => {
                            void handleAuthenticate()
                          }}
                          disabled={
                            isBusy ||
                            email.trim() === '' ||
                            password.trim() === '' ||
                            (authMode === 'create' && passwordConfirm.trim() === '')
                          }
                        >
                          {authMode === 'create' ? 'Create login' : 'Sign in'}
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <Stack spacing={2}>
                      <Alert severity="success">
                        Signed in as <strong>{email || firebase.auth().currentUser?.email}</strong>
                      </Alert>
                      <TextField
                        label="Claim code"
                        value={claimCode}
                        onChange={(event) => setClaimCode(event.target.value)}
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                        helperText="This code is provided by Just Lock It when your account is created over the phone."
                        fullWidth
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            void handleClaimAccess()
                          }}
                          disabled={isBusy || claimCode.trim() === ''}
                        >
                          Link my account
                        </Button>
                        <Button variant="outlined" onClick={() => void handleSignOut()}>
                          Use a different login
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      If you already have an online login, sign in first and then enter the
                      claim code. If your account was created by phone, the claim code keeps
                      your Authorize.Net customer profile linked to the portal account you
                      activate later.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </PageCenteredSection>
        </PageContentShell>
      </PageViewport>
      <FooterBarComponent />
    </>
  )
}
